
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
    copyTextToClipboard,
    formatAuthorsForDisplay,
    formatCitation,
    getCitationActor
} from '../utils/thesisCitation';

const ThesisGallery = ({ currentUser }) => {
    const location = useLocation();
    const [allTheses, setAllTheses] = useState([]);
    const [filteredTheses, setFilteredTheses] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [departments, setDepartments] = useState([]);

    const [keyword, setKeyword] = useState('');
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedDegreeId, setSelectedDegreeId] = useState('');
    const [selectedMajorId, setSelectedMajorId] = useState('');
    const [degreeParam, setDegreeParam] = useState('');
    const [majorParam, setMajorParam] = useState('');
    const [sortField, setSortField] = useState('year');
    const [sortDirection, setSortDirection] = useState('desc');

    const [selectedThesis, setSelectedThesis] = useState(null);
    const [viewPdf, setViewPdf] = useState(false);
    const [selectedFileUrl, setSelectedFileUrl] = useState('');
    const [lastUpdated, setLastUpdated] = useState('');
    const [copiedCitationId, setCopiedCitationId] = useState(null);

    const [showGeneralRequestForm, setShowGeneralRequestForm] = useState(false);
    const [requestDeptFilter, setRequestDeptFilter] = useState('');
    const [requestDegreeId, setRequestDegreeId] = useState('');
    const [requestMajorId, setRequestMajorId] = useState('');
    const [requestThesisId, setRequestThesisId] = useState('');
    const [requestSearch, setRequestSearch] = useState('');
    const [requestPickerOpen, setRequestPickerOpen] = useState(false);

    const [showRequestForm, setShowRequestForm] = useState(false);
    const [requestType, setRequestType] = useState('admin');
    const [requestData, setRequestData] = useState({ name: '', email: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const truncateOption = (text, max = 70) => {
        if (!text) return '';
        const str = String(text);
        return str.length > max ? `${str.slice(0, max - 1)}…` : str;
    };

    const getSignedUrl = async (filePath, { silent = false } = {}) => {
        if (!filePath) return '';
        if (/^https?:\/\//i.test(filePath)) return filePath;
        if (!currentUser?.user_id) return '';
        try {
            const res = await axios.get('/api/files/signed', {
                params: { path: filePath, userId: currentUser.user_id }
            });
            return res.data?.url || '';
        } catch (err) {
            if (!silent) {
                alert(err.response?.data?.error || 'Unable to access this file.');
            }
            return '';
        }
    };
    const getFilenameFromPath = (filePath) => {
        if (!filePath) return 'thesis.pdf';
        const withoutQuery = filePath.split('?')[0];
        const name = withoutQuery.split('/').pop() || 'thesis.pdf';
        return name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`;
    };

    const getAuthorData = (thesis) => {
        if (!thesis?.author_data) return [];
        if (Array.isArray(thesis.author_data)) return thesis.author_data;
        try {
            return JSON.parse(thesis.author_data);
        } catch {
            return [];
        }
    };

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());

    const canViewFullPdfForThesis = (thesis) => {
        if (!thesis?.file_path) return false;
        return Boolean(thesis && currentUser);
    };

    const getDegreeName = (degreeId) => {
        if (!degreeId) return '';
        for (const dept of departments) {
            const found = (dept.degrees || []).find(d => String(d.degree_id) === String(degreeId));
            if (found) return found.degree_name;
        }
        return '';
    };

    const getMajorName = (majorId) => {
        if (!majorId) return '';
        for (const dept of departments) {
            const degrees = dept.degrees || [];
            for (const degree of degrees) {
                const found = (degree.majors || []).find(m => String(m.major_id) === String(majorId));
                if (found) return found.major_name;
            }
        }
        return '';
    };

    const getCitationText = (thesis) => formatCitation(thesis, {
        getDegreeName,
        getMajorName
    });

    const fetchGalleryData = async () => {
        try {
            const citationActor = getCitationActor(currentUser);
            const res = await axios.get('/api/theses/search', {
                params: {
                    keyword: '',
                    year: '',
                    department: '',
                    sortBy: 'modified_desc',
                    ...citationActor
                }
            });
            const deptRes = await axios.get('/api/departments');
            setAllTheses(res.data || []);
            setDepartments(deptRes.data || []);

            const lastUpRes = await axios.get('/api/logs?limit=1');
            if (lastUpRes.data[0]) {
                setLastUpdated(new Date(lastUpRes.data[0].created_at).toLocaleString());
            }
        } catch (error) {
            console.error('Gallery fetch error', error);
        }
    };

    useEffect(() => {
        fetchGalleryData();
    }, []);

    useEffect(() => {
        if (!selectedThesis?.thesis_id) return;
        const updated = allTheses.find(t => String(t.thesis_id) === String(selectedThesis.thesis_id));
        if (updated) {
            setSelectedThesis(updated);
        }
    }, [allTheses, selectedThesis?.thesis_id]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const dept = params.get('department') || '';
        const year = params.get('year') || '';
        const degree = params.get('degree') || '';
        const major = params.get('major') || '';
        const initialKeyword = params.get('keyword') || '';
        setSelectedDepartment(dept);
        setSelectedYear(year);
        setDegreeParam(degree);
        setMajorParam(major);
        setKeyword(initialKeyword);
    }, [location.search]);

    useEffect(() => {
        if (!degreeParam) {
            setSelectedDegreeId('');
            return;
        }
        if (degreeParam === '__none__') {
            setSelectedDegreeId('__none__');
            return;
        }
        const dept = departments.find(d => d.dept_name === selectedDepartment);
        const byName = dept?.degrees?.find(d => String(d.degree_name) === String(degreeParam));
        const byId = dept?.degrees?.find(d => String(d.degree_id) === String(degreeParam));
        const resolved = byName?.degree_id || byId?.degree_id || '';
        setSelectedDegreeId(resolved || '');
    }, [degreeParam, selectedDepartment, departments]);

    useEffect(() => {
        if (!majorParam) {
            setSelectedMajorId('');
            return;
        }
        const dept = departments.find(d => d.dept_name === selectedDepartment);
        let majors = [];
        if (selectedDegreeId && selectedDegreeId !== '__none__') {
            const degreeObj = dept?.degrees?.find(d => String(d.degree_id) === String(selectedDegreeId));
            majors = degreeObj?.majors || [];
        } else {
            majors = (dept?.degrees || []).flatMap(d => d.majors || []);
        }
        const byName = majors.find(m => String(m.major_name) === String(majorParam));
        const byId = majors.find(m => String(m.major_id) === String(majorParam));
        const resolved = byName?.major_id || byId?.major_id || '';
        setSelectedMajorId(resolved || '');
    }, [majorParam, selectedDepartment, selectedDegreeId, departments]);


    useEffect(() => {
        setRequestDegreeId('');
        setRequestMajorId('');
    }, [requestDeptFilter]);

    useEffect(() => {
        setRequestMajorId('');
    }, [requestDegreeId]);

    useEffect(() => {
        let results = [...allTheses];

        if (selectedDepartment) {
            results = results.filter(t => t.department === selectedDepartment);
        }

        if (selectedDegreeId) {
            if (selectedDegreeId === '__none__') {
                results = results.filter(t => !t.degree_id);
            } else {
                results = results.filter(t => String(t.degree_id || '') === String(selectedDegreeId));
            }
        }

        if (selectedMajorId) {
            results = results.filter(t => String(t.major_id || '') === String(selectedMajorId));
        }

        if (selectedYear) {
            results = results.filter(t => String(t.year || '') === String(selectedYear));
        }

        if (keyword) {
            const lower = keyword.toLowerCase();
            results = results.filter(t =>
                String(t.title || '').toLowerCase().includes(lower) ||
                (t.authors || []).some(a => String(a || '').toLowerCase().includes(lower)) ||
                (t.keywords || []).some(k => String(k || '').toLowerCase().includes(lower)) ||
                String(t.abstract || '').toLowerCase().includes(lower)
            );
        }

        const directionMultiplier = sortDirection === 'asc' ? 1 : -1;
        const getTimestamp = (value) => {
            const time = new Date(value || 0).getTime();
            return Number.isNaN(time) ? 0 : time;
        };
        const compareDates = (aDate, bDate) => {
            const leftDate = getTimestamp(aDate);
            const rightDate = getTimestamp(bDate);
            return leftDate === rightDate ? 0 : directionMultiplier * (leftDate - rightDate);
        };
        const compareThesisId = (a, b) => directionMultiplier * (Number(a.thesis_id || 0) - Number(b.thesis_id || 0));

        results.sort((a, b) => {
            if (sortField === 'title') {
                const titleCompare = directionMultiplier * String(a.title || '').localeCompare(String(b.title || ''));
                return titleCompare || compareThesisId(a, b);
            }

            if (sortField === 'year') {
                const yearCompare = directionMultiplier * ((Number(a.year) || 0) - (Number(b.year) || 0));
                return yearCompare || compareDates(a.submission_date, b.submission_date) || compareThesisId(a, b);
            }

            if (sortField === 'view') {
                const viewCompare = directionMultiplier * (Number(a.view_count || 0) - Number(b.view_count || 0));
                return viewCompare || compareDates(a.submission_date, b.submission_date) || compareThesisId(a, b);
            }

            if (sortField === 'citation') {
                const citationCompare = directionMultiplier * (Number(a.citation_count || 0) - Number(b.citation_count || 0));
                return citationCompare || compareDates(a.submission_date, b.submission_date) || compareThesisId(a, b);
            }

            if (sortField === 'uploaded') {
                return compareDates(a.submission_date, b.submission_date) || compareThesisId(a, b);
            }

            return compareDates(a.updated_at || a.submission_date, b.updated_at || b.submission_date)
                || compareDates(a.submission_date, b.submission_date)
                || compareThesisId(a, b);
        });

        setFilteredTheses(results);
    }, [allTheses, selectedDepartment, selectedDegreeId, selectedMajorId, selectedYear, keyword, sortField, sortDirection]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedDepartment, selectedDegreeId, selectedMajorId, selectedYear, keyword, sortField, sortDirection]);

    useEffect(() => {
        const pageSize = 10;
        const totalPages = Math.max(1, Math.ceil(filteredTheses.length / pageSize));
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [filteredTheses.length, currentPage]);

    useEffect(() => {
        let active = true;
        const loadUrl = async () => {
            if (!selectedThesis?.file_path) {
                setSelectedFileUrl('');
                return;
            }
            if (!canViewFullPdfForThesis(selectedThesis)) {
                setSelectedFileUrl('');
                return;
            }
            const url = await getSignedUrl(selectedThesis.file_path, { silent: true });
            if (active) setSelectedFileUrl(url);
        };
        loadUrl();
        return () => { active = false; };
    }, [selectedThesis?.file_path, currentUser?.user_id, currentUser?.role, currentUser?.email]);

    const handleDownload = async (filePath) => {
        if (!filePath) {
            alert('PDF file unavailable.');
            return;
        }
        const url = await getSignedUrl(filePath);
        if (!url) return;
        try {
            const response = await axios.get(url, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = getFilenameFromPath(filePath);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            alert('Failed to download file. Please try again.');
        }
    };

    const applyViewUpdate = (thesisId, viewCount) => {
        const normalizedCount = Number(viewCount || 0);
        setAllTheses(prev => prev.map(thesis => (
            String(thesis.thesis_id) === String(thesisId)
                ? { ...thesis, view_count: normalizedCount }
                : thesis
        )));
        setSelectedThesis(prev => (
            prev && String(prev.thesis_id) === String(thesisId)
                ? { ...prev, view_count: normalizedCount }
                : prev
        ));
    };

    const applyCitationUpdate = (thesisId, citationCount, hasCited = true) => {
        const normalizedCount = Number(citationCount || 0);
        setAllTheses(prev => prev.map(thesis => (
            String(thesis.thesis_id) === String(thesisId)
                ? { ...thesis, citation_count: normalizedCount, has_cited: hasCited }
                : thesis
        )));
        setSelectedThesis(prev => (
            prev && String(prev.thesis_id) === String(thesisId)
                ? { ...prev, citation_count: normalizedCount, has_cited: hasCited }
                : prev
        ));
    };

    const handleCopyCitation = async (thesis) => {
        if (!thesis?.thesis_id) return;

        try {
            await copyTextToClipboard(getCitationText(thesis));
            setCopiedCitationId(thesis.thesis_id);

            if (!thesis.has_cited) {
                const res = await axios.post(`/api/theses/${thesis.thesis_id}/cite`, getCitationActor(currentUser));
                applyCitationUpdate(thesis.thesis_id, res.data?.citation_count, true);
            }

            window.setTimeout(() => {
                setCopiedCitationId((currentId) => (
                    String(currentId) === String(thesis.thesis_id) ? null : currentId
                ));
            }, 2000);
        } catch (err) {
            if (err.response?.status === 409) {
                applyCitationUpdate(thesis.thesis_id, err.response.data?.citation_count, true);
                setCopiedCitationId(thesis.thesis_id);
                return;
            }

            console.error('Failed to copy citation:', err);
            alert('Failed to copy citation. Please try again.');
        }
    };

    const openThesisDetails = async (thesis) => {
        if (!thesis?.thesis_id) return;
        const latestThesis = allTheses.find((item) => String(item.thesis_id) === String(thesis.thesis_id)) || thesis;
        setSelectedThesis(latestThesis);
        setViewPdf(false);
        try {
            const res = await axios.post(`/api/theses/${thesis.thesis_id}/view`);
            applyViewUpdate(thesis.thesis_id, res.data?.view_count);
        } catch (err) {
            console.error('Failed to record thesis view:', err);
        }
    };

    const submitRequest = async () => {
        if (!requestData.name || !requestData.email) {
            alert('Please provide your name and email.');
            return;
        }
        if (!isValidEmail(requestData.email)) {
            alert('Please enter a valid email address.');
            return;
        }

        setIsSubmitting(true);
        const payload = {
            ...requestData,
            thesis_id: selectedThesis?.thesis_id || null,
            thesis_title: selectedThesis?.title || 'Thesis Request',
            type: 'specific'
        };

        try {
            await axios.post('/api/requests', payload);
            alert('Request sent successfully! Our staff will contact you via email.');
            setShowRequestForm(false);
            setRequestData({ name: '', email: '', message: '' });
        } catch (err) {
            alert('Failed to send request. Please check your connection.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitAuthorRequest = async () => {
        if (!requestData.name || !requestData.email) {
            alert('Please provide your name and email.');
            return;
        }
        if (!isValidEmail(requestData.email)) {
            alert('Please enter a valid email address.');
            return;
        }

        const authorsWithEmails = selectedThesis.author_data?.filter(a => a.email) || [];
        if (authorsWithEmails.length === 0) {
            alert('No authors have provided email addresses for this thesis.');
            return;
        }

        setIsSubmitting(true);
        const payload = {
            thesis_id: selectedThesis?.thesis_id,
            author_ids: authorsWithEmails.map(a => a.author_id),
            requester_name: requestData.name,
            requester_email: requestData.email,
            message: requestData.message
        };

        try {
            await axios.post('/api/author-requests', payload);
            alert('Request sent to authors! They will review and respond via email.');
            setShowRequestForm(false);
            setRequestData({ name: '', email: '', message: '' });
            setRequestType('admin');
        } catch (err) {
            const serverError = err.response?.data?.error;
            const serverDetails = err.response?.data?.details;
            alert(serverDetails ? `${serverError}: ${serverDetails}` : (serverError || 'Failed to send request. Please check your connection.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitGeneralRequest = async () => {
        if (!requestData.name || !requestData.email) {
            alert('Please provide your name and email.');
            return;
        }
        if (!isValidEmail(requestData.email)) {
            alert('Please enter a valid email address.');
            return;
        }

        setIsSubmitting(true);
        const selected = allTheses.find(t => String(t.thesis_id) === String(requestThesisId));
        const isSpecific = Boolean(selected);
        const payload = {
            ...requestData,
            thesis_id: isSpecific ? selected.thesis_id : null,
            thesis_title: isSpecific ? selected.title : 'General Request',
            type: isSpecific ? 'specific' : 'general'
        };

        try {
            await axios.post('/api/requests', payload);
            alert('Request sent successfully! Our staff will contact you via email.');
            setShowGeneralRequestForm(false);
            setRequestDeptFilter('');
            setRequestDegreeId('');
            setRequestMajorId('');
            setRequestThesisId('');
            setRequestSearch('');
            setRequestPickerOpen(false);
            setRequestData({ name: '', email: '', message: '' });
        } catch (err) {
            alert('Failed to send request. Please check your connection.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitGeneralAuthorRequest = async () => {
        if (!requestData.name || !requestData.email) {
            alert('Please provide your name and email.');
            return;
        }
        if (!isValidEmail(requestData.email)) {
            alert('Please enter a valid email address.');
            return;
        }

        const selected = allTheses.find(t => String(t.thesis_id) === String(requestThesisId));
        if (!selected) {
            alert('Please select a specific thesis to request from authors.');
            return;
        }

        const authorsWithEmails = selected.author_data?.filter(a => a.email) || [];
        if (authorsWithEmails.length === 0) {
            alert('No authors have provided email addresses for this thesis.');
            return;
        }

        setIsSubmitting(true);
        const payload = {
            thesis_id: selected.thesis_id,
            author_ids: authorsWithEmails.map(a => a.author_id),
            requester_name: requestData.name,
            requester_email: requestData.email,
            message: requestData.message
        };

        try {
            await axios.post('/api/author-requests', payload);
            alert('Request sent to authors! They will review and respond via email.');
            setShowGeneralRequestForm(false);
            setRequestDeptFilter('');
            setRequestDegreeId('');
            setRequestMajorId('');
            setRequestThesisId('');
            setRequestSearch('');
            setRequestPickerOpen(false);
            setRequestData({ name: '', email: '', message: '' });
        } catch (err) {
            const serverError = err.response?.data?.error;
            const serverDetails = err.response?.data?.details;
            alert(serverDetails ? `${serverError}: ${serverDetails}` : (serverError || 'Failed to send request. Please check your connection.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const canRequestAuthors = Boolean(selectedThesis?.author_data?.some(a => a.email));

    const selectedThesisForRequest = allTheses.find(t => String(t.thesis_id) === String(requestThesisId));
    const canAuthorRequest = Boolean(selectedThesisForRequest && selectedThesisForRequest.author_data?.some(a => a.email));
    const degreesForRequestDept = requestDeptFilter
        ? (departments.find(d => d.dept_name === requestDeptFilter)?.degrees || [])
        : [];
    const majorsForRequestDegree = requestDegreeId
        ? (degreesForRequestDept.find(d => String(d.degree_id) === String(requestDegreeId))?.majors || [])
        : [];

    const selectedDeptObj = departments.find(d => d.dept_name === selectedDepartment);
    const degreesForSelectedDept = selectedDeptObj?.degrees || [];
    const majorsForSelectedDegree = selectedDegreeId && selectedDegreeId !== '__none__'
        ? (degreesForSelectedDept.find(d => String(d.degree_id) === String(selectedDegreeId))?.majors || [])
        : [];

    const yearOptions = Array.from(new Set(allTheses.map(t => t.year).filter(Boolean))).sort((a, b) => b - a);
    const pageSize = 10;
    const totalPages = Math.max(1, Math.ceil(filteredTheses.length / pageSize));
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filteredTheses.length);
    const pagedTheses = filteredTheses.slice(startIndex, startIndex + pageSize);
    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <style>
                {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                `}
            </style>

            <div style={{ marginBottom: '20px' }}>
                <h2 style={{ color: '#333' }}>NVC Thesis Repository Gallery</h2>
                <p style={{ color: '#666' }}>Browse all theses in the archive.</p>
            </div>

            {lastUpdated && (
                <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '-6px', marginBottom: '14px' }}>
                    Last Collection Update: {lastUpdated}
                </p>
            )}

            <div style={{ ...bannerStyle, marginBottom: '20px' }}>
                <div>
                    <h4 style={{ margin: 0, color: '#004085' }}>Want a specific thesis?</h4>
                    <p style={{ margin: 0, color: '#004085', fontSize: '0.95rem' }}>Submit a request and our staff will assist you.</p>
                </div>
                <button
                    onClick={() => {
                        setRequestDeptFilter('');
                        setRequestDegreeId('');
                        setRequestMajorId('');
                        setRequestThesisId('');
                        setRequestSearch('');
                        setRequestPickerOpen(false);
                        setRequestData({ name: '', email: '', message: '' });
                        setShowGeneralRequestForm(true);
                    }}
                    style={{ ...btnStyle, background: '#007bff', fontWeight: 'bold' }}
                >
                    Request PDF File
                </button>
            </div>

            <div style={filterBarStyle}>
                <input
                    type="text"
                    placeholder="Search by title, author, keywords, or abstract..."
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    style={{ ...inputStyle, flex: 2 }}
                />
                <select
                    value={selectedDepartment}
                    onChange={(e) => {
                        setSelectedDepartment(e.target.value);
                        setSelectedDegreeId('');
                        setDegreeParam('');
                        setSelectedMajorId('');
                        setMajorParam('');
                    }}
                    style={{ ...inputStyle, maxWidth: '100%', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                >
                    <option value="">All Departments</option>
                    {departments.map(d => (
                        <option key={d.dept_id} value={d.dept_name}>{truncateOption(d.dept_name, 60)}</option>
                    ))}
                </select>
                {selectedDepartment && degreesForSelectedDept.length > 0 && (
                    <select
                        value={selectedDegreeId}
                        onChange={(e) => {
                            setSelectedDegreeId(e.target.value);
                            setSelectedMajorId('');
                        }}
                        style={{ ...inputStyle, maxWidth: '100%', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                    >
                        <option value="">All Degrees</option>
                        <option value="__none__">No Degree</option>
                        {degreesForSelectedDept.map(d => (
                            <option key={d.degree_id} value={d.degree_id}>{truncateOption(d.degree_name, 60)}</option>
                        ))}
                    </select>
                )}
                {selectedDegreeId && selectedDegreeId !== '__none__' && majorsForSelectedDegree.length > 0 && (
                    <select value={selectedMajorId} onChange={(e) => setSelectedMajorId(e.target.value)} style={{ ...inputStyle, maxWidth: '100%', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                        <option value="">All Majors</option>
                        {majorsForSelectedDegree.map(m => (
                            <option key={m.major_id} value={m.major_id}>{truncateOption(m.major_name, 60)}</option>
                        ))}
                    </select>
                )}
                <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} style={{ ...inputStyle, maxWidth: '100%', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    <option value="">All Years</option>
                    {yearOptions.map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
                <select value={sortField} onChange={(e) => setSortField(e.target.value)} style={{ ...inputStyle, maxWidth: '100%', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    <option value="modified">Last Modified</option>
                    <option value="uploaded">Last Added</option>
                    <option value="year">Year</option>
                    <option value="title">Title</option>
                    <option value="view">Views</option>
                    <option value="citation">Citations</option>
                </select>
                <button
                    type="button"
                    onClick={() => setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                    style={sortToggleBtn}
                >
                    {sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                </button>
            </div>

            <p style={{ color: '#666', marginBottom: '20px' }}>
                Found {filteredTheses.length} thesis(es)
                {filteredTheses.length > 0 ? ` • Showing ${startIndex + 1}-${endIndex}` : ''}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '18px' }}>
                {filteredTheses.length > 0 ? (
                    pagedTheses.map((t) => (
                        <div key={t.thesis_id} style={cardStyle}>
                            <h4 style={{ marginBottom: '8px', lineHeight: '1.35', fontSize: '1.25rem' }}>{t.title}</h4>

                            <p style={citationStyle}>{getCitationText(t)}</p>
                            <div style={sectionDividerStyle} />

                            {t.authors && t.authors.length > 0 && (
                                <p style={{ fontSize: '1rem', color: '#555', marginBottom: '10px' }}>
                                    <strong>Authors:</strong> {formatAuthorsForDisplay(t.authors)}
                                </p>
                            )}

                            {t.keywords && t.keywords.length > 0 && (
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                    {t.keywords.slice(0, 3).map((k, i) => (
                                        <span key={i} style={{ background: '#eef', padding: '4px 8px', borderRadius: '12px', fontSize: '12px' }}>
                                            {k}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {(t.authors?.length > 0 || t.keywords?.length > 0) && <div style={sectionDividerStyle} />}

                            <p style={{ fontSize: '1rem', color: '#555', lineHeight: '1.6', marginBottom: '14px' }}>
                                {t.abstract?.substring(0, 380)}...
                            </p>

                            <div style={cardActionsRow}>
                                <button
                                    onClick={() => openThesisDetails(t)}
                                    style={viewMoreBtn}
                                >
                                    View Details
                                </button>
                                <span style={viewCountBadge}>Views: {Number(t.view_count || 0)}</span>
                                <span style={viewCountBadge}>Citations: {Number(t.citation_count || 0)}</span>
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: '#999' }}>
                        <p>No theses found matching your search.</p>
                    </div>
                )}
            </div>
            {filteredTheses.length > 0 && totalPages > 1 && (
                <div style={paginationBar}>
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        style={{ ...pageBtn, opacity: currentPage === 1 ? 0.6 : 1 }}
                    >
                        Previous
                    </button>
                    <span style={pageInfo}>Page {currentPage} of {totalPages}</span>
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        style={{ ...pageBtn, opacity: currentPage === totalPages ? 0.6 : 1 }}
                    >
                        Next
                    </button>
                </div>
            )}
            {selectedThesis && (
                (() => {
                    const hasPdf = Boolean(selectedThesis?.file_path);
                    const canViewFullPdf = hasPdf && Boolean(currentUser);
                    const canDownload = hasPdf && Boolean(currentUser) && ['admin', 'staff'].includes(currentUser.role);
                    const canSeePdfUnavailable = !hasPdf && Boolean(currentUser) && ['admin', 'staff'].includes(currentUser.role);

                    const handleTogglePdf = () => {
                        if (!canViewFullPdf) return;
                        setViewPdf(!viewPdf);
                    };

                    return (
                        <div style={modalOverlay} onClick={() => {
                            setSelectedThesis(null);
                            setViewPdf(false);
                        }}>
                            <div style={{ ...modalContent, width: viewPdf ? '95%' : '550px', height: viewPdf ? '90vh' : undefined }} onClick={(e) => e.stopPropagation()}>
                                <h3 style={{ margin: '0 0 5px 0', color: '#333' }}>{selectedThesis.title}</h3>

                                <p style={{ color: '#666', fontSize: '0.9rem', marginTop: 0, marginBottom: '15px' }}>
                                    Views: {Number(selectedThesis.view_count || 0)} | Citations: {Number(selectedThesis.citation_count || 0)}
                                </p>

                                {!viewPdf ? (
                                    <div style={{ flex: 1, overflowY: 'auto' }}>
                                        {canSeePdfUnavailable && (
                                            <div style={{ marginBottom: '12px', padding: '10px', borderRadius: '6px', background: '#fff3cd', border: '1px solid #ffeeba', color: '#856404', fontSize: '0.9rem' }}>
                                                PDF file unavailable for this thesis.
                                            </div>
                                        )}
                                        {(() => {
                                            const degreeName = selectedThesis.degree_id ? getDegreeName(selectedThesis.degree_id) : '';
                                            const majorName = selectedThesis.major_id ? getMajorName(selectedThesis.major_id) : '';
                                            const deptDisplay = [selectedThesis.department, degreeName, majorName].filter(Boolean).join(' - ');
                                            return <p><strong>{selectedThesis.year}</strong> &middot; {deptDisplay}</p>;
                                        })()}

                                        {selectedThesis.authors && selectedThesis.authors.length > 0 && (
                                            <p style={{ margin: '6px 0', color: '#333' }}>
                                                <strong>Authors:</strong> {formatAuthorsForDisplay(selectedThesis.authors)}
                                            </p>
                                        )}

                                        {selectedThesis.keywords && selectedThesis.keywords.length > 0 && (
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                                {selectedThesis.keywords.map((k, i) => (
                                                    <span key={i} style={{ background: '#eef', padding: '6px 8px', borderRadius: '12px', fontSize: '13px' }}>
                                                        {k}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        <h5 style={{ marginTop: '15px', marginBottom: '8px', color: '#333' }}>Abstract</h5>
                                        <div style={abstractBox}>{selectedThesis.abstract}</div>

                                        <div style={citationPanelStyle}>
                                            <div style={citationHeaderRow}>
                                                <h5 style={{ margin: 0, color: '#333' }}>Citation</h5>
                                            </div>
                                            <p style={{ ...citationStyleText, marginBottom: '12px' }}>
                                                {getCitationText(selectedThesis)}
                                            </p>
                                            <div style={citationFooterRow}>
                                                <span style={citationCountText}>
                                                    Citation count: {Number(selectedThesis.citation_count || 0)}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleCopyCitation(selectedThesis)}
                                                    style={copyCitationBtn}
                                                >
                                                    {String(copiedCitationId) === String(selectedThesis.thesis_id) ? 'Copied' : 'Copy Citation'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : canViewFullPdf ? (
                                    <div style={{ flex: 1, background: '#525659', borderRadius: '8px', overflow: 'hidden', height: '85vh' }}>
                                        <iframe title="PDF" src={`${selectedFileUrl}#toolbar=0`} width="100%" height="100%" style={{ border: 'none' }} />
                                    </div>
                                ) : (
                                    <div style={restrictedOverlay} className="restricted-overlay">
                                        <h2>Full PDF Access Restricted</h2>
                                        <p>Only logged-in staff and admins can view the complete document.</p>
                                        <p style={{ fontSize: '0.9rem', marginTop: '15px' }}>Request access to this thesis to receive the full PDF via email.</p>
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
                                    {currentUser ? (
                                        <button
                                            onClick={handleTogglePdf}
                                            style={{ ...readBtn, opacity: canViewFullPdf ? 1 : 0.5, cursor: canViewFullPdf ? 'pointer' : 'not-allowed' }}
                                            disabled={!canViewFullPdf}
                                            title={canViewFullPdf ? '' : (hasPdf ? 'Only staff and admins can view the full PDF.' : 'PDF file unavailable.')}
                                        >
                                            {viewPdf ? 'Show Abstract' : 'Read Full PDF'}
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => { setRequestType('author'); setShowRequestForm(true); }}
                                                disabled={!canRequestAuthors}
                                                style={{
                                                    ...requestBtn,
                                                    background: canRequestAuthors ? '#007bff' : '#6c757d',
                                                    opacity: canRequestAuthors ? 1 : 0.6,
                                                    cursor: canRequestAuthors ? 'pointer' : 'not-allowed'
                                                }}
                                                title={canRequestAuthors ? '' : 'No author emails are available for this thesis.'}
                                            >
                                                Request PDF file from Author/s
                                            </button>
                                            {currentUser?.role !== 'admin' && (
                                                <button onClick={() => { setRequestType('admin'); setShowRequestForm(true); }} style={requestBtn}>
                                                    Request PDF file from Admin/s
                                                </button>
                                            )}
                                        </>
                                    )}

                                    {canDownload && (
                                        <button onClick={() => handleDownload(selectedThesis.file_path)} style={downloadBtn}>
                                            Download
                                        </button>
                                    )}

                                    <button onClick={() => {
                                        setSelectedThesis(null);
                                        setViewPdf(false);
                                    }} style={closeBtn}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()
            )}

            {showGeneralRequestForm && (
                <div style={modalOverlay} onClick={() => setShowGeneralRequestForm(false)}>
                    <div style={{ ...modalContent, width: '550px' }} onClick={(e) => e.stopPropagation()}>
                        {isSubmitting && (
                            <div style={loaderInnerOverlay}>
                                <div style={spinnerStyle}></div>
                                <p style={{ fontWeight: 'bold', marginTop: '10px' }}>Submitting Request...</p>
                            </div>
                        )}

                        <div style={requestModalBody}>
                            <h3 style={{ marginTop: 0 }}>Request a Thesis</h3>
                            <p style={{ color: '#666', fontSize: '0.9rem' }}>
                                Request a thesis project's full PDF file by filling out the form below. You can optionally select a specific thesis to request from its authors. If no thesis is selected, your request will be treated as a general inquiry and our staff will assist you in finding relevant theses.
                            </p>

                            <div style={thesisPickerStyle}>
                                <button
                                    type="button"
                                    onClick={() => setRequestPickerOpen(!requestPickerOpen)}
                                    style={thesisPickerToggleStyle}
                                >
                                    <span style={{ fontWeight: 'bold' }}>Select a Thesis (Optional)</span>
                                    <span style={{ color: '#666', fontSize: '0.8rem' }}>
                                        {requestPickerOpen ? 'Hide filters' : 'Filter, search, then choose'}
                                    </span>
                                </button>
                                {requestPickerOpen && (
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                                        <select
                                            style={{ ...inputStyle, flex: '1 1 180px', maxWidth: '100%', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                                            value={requestDeptFilter}
                                            onChange={(e) => setRequestDeptFilter(e.target.value)}
                                        >
                                            <option value="">All Departments</option>
                                            {departments.map(d => (
                                                <option key={d.dept_id} value={d.dept_name}>{truncateOption(d.dept_name, 60)}</option>
                                            ))}
                                        </select>
                                        {degreesForRequestDept.length > 0 && (
                                            <select
                                                style={{ ...inputStyle, flex: '1 1 180px', maxWidth: '100%', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                                                value={requestDegreeId}
                                                onChange={(e) => setRequestDegreeId(e.target.value)}
                                            >
                                                <option value="">All Degrees</option>
                                                {degreesForRequestDept.map(d => (
                                                    <option key={d.degree_id} value={d.degree_id}>{truncateOption(d.degree_name, 60)}</option>
                                                ))}
                                            </select>
                                        )}
                                        {majorsForRequestDegree.length > 0 && (
                                            <select
                                                style={{ ...inputStyle, flex: '1 1 180px', maxWidth: '100%', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                                                value={requestMajorId}
                                                onChange={(e) => setRequestMajorId(e.target.value)}
                                            >
                                                <option value="">All Majors</option>
                                                {majorsForRequestDegree.map(m => (
                                                    <option key={m.major_id} value={m.major_id}>{truncateOption(m.major_name, 60)}</option>
                                                ))}
                                            </select>
                                        )}
                                        <input
                                            type="text"
                                            placeholder="Search title..."
                                            value={requestSearch}
                                            onChange={(e) => setRequestSearch(e.target.value)}
                                            style={{ ...inputStyle, flex: '1 1 220px' }}
                                        />
                                        <select
                                            style={{ ...inputStyle, flex: '2 1 260px', maxWidth: '100%', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                                            value={requestThesisId}
                                            onChange={(e) => setRequestThesisId(e.target.value)}
                                        >
                                            <option value="">No specific thesis (General Request)</option>
                                            {(allTheses || [])
                                                .filter(t => !requestDeptFilter || t.department === requestDeptFilter)
                                                .filter(t => !requestDegreeId || String(t.degree_id || '') === String(requestDegreeId))
                                                .filter(t => !requestMajorId || String(t.major_id || '') === String(requestMajorId))
                                                .filter(t => !requestSearch || String(t.title || '').toLowerCase().includes(requestSearch.toLowerCase()))
                                                .sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')))
                                                .map(t => (
                                                    <option key={t.thesis_id} value={t.thesis_id}>
                                                        {truncateOption(t.title, 70)} ({t.year || 'N/A'})
                                                    </option>
                                                ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            <input
                                placeholder="Full Name"
                                style={{ ...inputStyle, width: '100%', marginBottom: '10px' }}
                                value={requestData.name}
                                onChange={e => setRequestData({ ...requestData, name: e.target.value })}
                            />
                            <input
                                type="email"
                                placeholder="Email Address"
                                style={{ ...inputStyle, width: '100%', marginBottom: '10px' }}
                                value={requestData.email}
                                onChange={e => setRequestData({ ...requestData, email: e.target.value })}
                            />
                            <textarea
                                placeholder="Purpose of request (e.g., Undergraduate Research)..."
                                style={{ ...inputStyle, width: '100%', height: '100px', marginBottom: '10px', resize: 'none' }}
                                value={requestData.message}
                                onChange={e => setRequestData({ ...requestData, message: e.target.value })}
                            />

                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                {currentUser?.role !== 'admin' && (
                                    <button
                                        onClick={submitGeneralRequest}
                                        disabled={isSubmitting}
                                        style={{ ...btnStyle, flex: 1, opacity: isSubmitting ? 0.7 : 1 }}
                                    >
                                        {isSubmitting ? 'Sending...' : 'Admin Request'}
                                    </button>
                                )}
                                <button
                                    onClick={submitGeneralAuthorRequest}
                                    disabled={isSubmitting || !requestThesisId || !canAuthorRequest}
                                    style={{
                                        ...btnStyle,
                                        flex: 1,
                                        background: (!requestThesisId || !canAuthorRequest) ? '#6c757d' : '#007bff',
                                        opacity: (isSubmitting || !requestThesisId || !canAuthorRequest) ? 0.6 : 1,
                                        cursor: (isSubmitting || !requestThesisId || !canAuthorRequest) ? 'not-allowed' : 'pointer'
                                    }}
                                    title={
                                        !requestThesisId
                                            ? 'Select a specific thesis to request from authors.'
                                            : (!canAuthorRequest ? 'This thesis has no author emails on file.' : '')
                                    }
                                >
                                    {isSubmitting ? 'Sending...' : 'Author Request'}
                                </button>
                                <button
                                    onClick={() => { setShowGeneralRequestForm(false); }}
                                    style={closeBtn}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showRequestForm && selectedThesis && (
                <div style={modalOverlay} onClick={() => setShowRequestForm(false)}>
                    <div style={{ ...modalContent, width: '550px' }} onClick={(e) => e.stopPropagation()}>
                        {isSubmitting && (
                            <div style={loaderInnerOverlay}>
                                <div style={spinnerStyle}></div>
                                <p style={{ fontWeight: 'bold', marginTop: '10px' }}>Submitting Request...</p>
                            </div>
                        )}

                        <div style={requestModalBody}>
                            <h3 style={{ marginTop: 0 }}>
                                {requestType === 'author'
                                    ? 'Request PDF from Authors'
                                    : 'Request PDF from Administration'}
                            </h3>
                            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '15px' }}>
                                Requesting: <strong>{selectedThesis.title}</strong>
                            </p>

                            {requestType === 'author' && (
                                <p style={{ background: '#f0f7ff', padding: '10px', borderRadius: '4px', fontSize: '0.85rem', color: '#004085', marginBottom: '15px', borderLeft: '4px solid #0066cc' }}>
                                    Your request will be sent directly to the thesis author(s). They will review your request and may approve it to send you the full PDF file.
                                </p>
                            )}

                            {requestType === 'admin' && currentUser?.role !== 'admin' && (
                                <p style={{ background: '#fff3cd', padding: '10px', borderRadius: '4px', fontSize: '0.85rem', color: '#856404', marginBottom: '15px', borderLeft: '4px solid #ffc107' }}>
                                    Your request will be submitted to our administration team. They will review and respond to you via email.
                                </p>
                            )}

                            <input
                                placeholder="Full Name"
                                style={{ ...inputStyle, width: '100%', marginBottom: '10px' }}
                                value={requestData.name}
                                onChange={e => setRequestData({ ...requestData, name: e.target.value })}
                            />
                            <input
                                type="email"
                                placeholder="Email Address"
                                style={{ ...inputStyle, width: '100%', marginBottom: '10px' }}
                                value={requestData.email}
                                onChange={e => setRequestData({ ...requestData, email: e.target.value })}
                            />
                            <textarea
                                placeholder={requestType === 'author'
                                    ? 'Message for author (optional)...'
                                    : 'Why do you need this thesis? (e.g., Research, Citation, etc.)...'}
                                style={{ ...inputStyle, width: '100%', height: '100px', marginBottom: '10px', resize: 'none' }}
                                value={requestData.message}
                                onChange={e => setRequestData({ ...requestData, message: e.target.value })}
                            />

                            <div style={{ display: 'flex', gap: '10px' }}>
                                {(requestType !== 'admin' || currentUser?.role !== 'admin') && (
                                    <button
                                        onClick={requestType === 'author' ? submitAuthorRequest : submitRequest}
                                        disabled={isSubmitting}
                                        style={{ ...btnStyle, flex: 1, opacity: isSubmitting ? 0.7 : 1 }}
                                    >
                                        {isSubmitting ? 'Sending...' : 'Submit Request'}
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        setShowRequestForm(false);
                                        setRequestType('admin');
                                    }}
                                    style={closeBtn}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
const filterBarStyle = { display: 'flex', gap: '10px', background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px', flexWrap: 'wrap' };
const inputStyle = { padding: '10px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem', boxSizing: 'border-box' };
const sortToggleBtn = { padding: '10px 14px', borderRadius: '4px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontWeight: 'bold' };
const cardStyle = { border: '1px solid #eee', padding: '22px 24px', borderRadius: '14px', boxShadow: '0 6px 10px rgba(0,0,0,0.06)', background: '#fff', display: 'flex', flexDirection: 'column' };
const citationStyle = { fontSize: '0.95rem', color: '#666', fontStyle: 'italic', margin: '0 0 14px 0', lineHeight: '1.5' };
const cardActionsRow = { display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: 'auto', alignItems: 'center' };
const viewCountBadge = { background: 'transparent', color: '#5b6773', padding: '10px 0', fontWeight: 'bold', width: 'fit-content' };
const viewMoreBtn = { border: '1px solid #800000', background: '#800000', color: '#fff', padding: '10px 16px', cursor: 'pointer', fontWeight: 'bold', marginTop: 'auto', textAlign: 'left', borderRadius: '6px', width: 'fit-content' };
const sectionDividerStyle = { height: '1px', background: '#ececec', margin: '6px 0 12px 0' };
const modalOverlay = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 };
const modalContent = { background: 'white', padding: '20px', borderRadius: '12px', maxWidth: '95%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(0,0,0,0.4)', overflowY: 'auto', boxSizing: 'border-box' };
const requestModalBody = { maxHeight: '70vh', overflowY: 'auto', paddingRight: '6px' };
const abstractBox = { background: '#f9f9f9', padding: '15px', borderRadius: '6px', fontSize: '0.95rem', lineHeight: '1.6', maxHeight: '400px', overflowY: 'auto', border: '1px solid #eee' };
const citationPanelStyle = { marginTop: '14px', padding: '14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fafafa' };
const citationHeaderRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' };
const citationStyleText = { fontSize: '0.92rem', color: '#4b5563', lineHeight: '1.6', margin: 0 };
const citationFooterRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' };
const citationCountText = { color: '#666', fontSize: '0.9rem', fontWeight: 'bold' };
const copyCitationBtn = { padding: '9px 14px', background: '#800000', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
const readBtn = { flex: 1, padding: '12px', background: '#800000', color: 'white', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: 'bold' };
const downloadBtn = { flex: 1, padding: '12px', background: '#28a745', color: 'white', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: 'bold' };
const requestBtn = { flex: 1, padding: '12px', background: '#007bff', color: 'white', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: 'bold' };
const closeBtn = { padding: '12px 25px', border: '1px solid #ddd', background: 'white', borderRadius: '4px', cursor: 'pointer' };
const restrictedOverlay = { height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', padding: '40px', textAlign: 'center', background: '#333' };
const btnStyle = { padding: '10px 20px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };
const bannerStyle = { background: '#f0f7ff', padding: '16px 20px', borderRadius: '12px', border: '1px solid #cce5ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' };
const thesisPickerStyle = { background: '#f8f9fa', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', marginBottom: '10px' };
const thesisPickerToggleStyle = { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' };
const loaderInnerOverlay = { position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.9)', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' };
const spinnerStyle = { border: '4px solid #f3f3f3', borderTop: '4px solid #800000', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite' };
const paginationBar = { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '20px', flexWrap: 'wrap' };
const pageBtn = { padding: '8px 14px', border: '1px solid #ddd', background: '#fff', borderRadius: '6px', cursor: 'pointer' };
const pageInfo = { color: '#666', fontSize: '0.9rem' };

export default ThesisGallery;
